import React, { ComponentProps, useCallback, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useTemporarySave } from '../connectedForm/utils/handleTemporarySave';
import { serviceActions } from '../../state/modules/service';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import RowMoveCell from '../react-data-grid-addons/src/draggable/RowMoveCell';
import EditableDataGrid from './EditableDataGrid';
import { AlertType, IAbstractLine } from '../../graphql/schema/p4types';
import { ReorderHeader } from '../react-data-grid/ReorderHeader';
import { cloneDeep } from '@apollo/client/utilities';
import { useFullUserDetails } from '../../utils/hooks';
import { IFormatterProps } from '../../@types/workspaceTypes';

//Sort mechanism for lines on entities that extend AbstractHeader.
export const AbstractLineGrid = ({
  columns,
  selectCellFormatter,
  ...props
}: ComponentProps<typeof EditableDataGrid>) => {
  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);
  const performTemporarySave = useTemporarySave(false);

  const fullUserDetails = useFullUserDetails();
  const allowLineSort = fullUserDetails?.companyUser?.allowLineSorting;

  const { change, values } = connectedForm;

  //Sorting function for when the user clicks a column header to sort the grid lines.
  const handleGridSort = useCallback(
    (sortColumn, sortDirection) => {
      Promise.all([
        dispatch(change('linesSortColumn', sortColumn)),
        dispatch(change('linesSortDirection', sortDirection !== 'NONE' ? sortDirection : null)),
        dispatch(change('linesSortOnTemporary', true)), //Trigger a sort on this temporary save.
      ]).then(performTemporarySave);
    },
    [change, dispatch, performTemporarySave]
  );

  //Sorting function for when the user does drag + drop to sort the grid lines.
  const onRowSort = useCallback(
    ({ rowSource, rowTarget }: { rowSource: { idx: number }; rowTarget: { idx: number } }) => {
      dispatch(serviceActions.genericAlert('Re-Ordering', 'Rows are being re-ordered', AlertType.Info));
      const movement = rowTarget.idx - rowSource.idx;
      if (movement === 0) return;

      //If the previous sort wasn't in lineOrder - Need to update the 'lineOrder' field on all rows.
      //Or if the line has moved to the top, we also need to do this (Because '0' values for lineOrder will be forcefully reset in the services).
      const updatedLines = cloneDeep(values?.orderLines ?? []) as Array<IAbstractLine>;

      updatedLines.forEach((element, index) => {
        if (index === rowSource.idx) {
          element.lineOrder = movement > 0 ? rowTarget.idx : rowTarget.idx + 1;
        } else if (movement > 0 && index >= rowSource.idx && index < rowTarget.idx) {
          element.lineOrder = index;
        } else if (movement < 0 && index >= rowTarget.idx && index < rowSource.idx) {
          element.lineOrder = index + 2;
        } else {
          element.lineOrder = index + 1;
        }
      });

      // Now we have lineOrder set against all the lines, sort them into the correct order
      updatedLines.sort((l1, l2) => l1.lineOrder - l2.lineOrder);

      Promise.all([
        change(`orderLines`, updatedLines),
        change(`linesSortOnTemporary`, true),
        change('linesSortColumn', 'lineOrder'),
        change('linesSortDirection', 'ASC'),
      ])
        .then(performTemporarySave)
        .catch(() => {});
    },
    [change, dispatch, performTemporarySave, values?.orderLines]
  );

  const columnsWithSortCell = useMemo<Array<IEditableDataGridColumnDef>>(() => {
    const cols: Array<IEditableDataGridColumnDef> = [
      {
        key: 'select-row',
        name: '',
        cellFormatter: selectCellFormatter, //Note - This will override the formatter when it's provided.
        formatter: RowMoveCell,
        filterable: false,
        headerRenderer: allowLineSort ? <ReorderHeader /> : undefined,
        width: 45,
        locked: true,
        getRowMetaData: (rowData: any) => ({ rowData }),
        cellClass: 'rdg-row-actions-cell',
        editable: false,
        tabSkip: true,
      },
      ...columns,
    ];
    return cols;
  }, [allowLineSort, columns, selectCellFormatter]);

  const columnsWithoutSort = useMemo(() => {
    return [...columnsWithSortCell.map((column) => ({ ...column, sortable: false }))];
  }, [columnsWithSortCell]);

  if (!allowLineSort) {
    // Explicitly avoid passing sort functions if user is not permitted to do so.
    // (Although all the mechanisms to "trigger" the sort should already be disabled - This is a backup).
    return <EditableDataGrid {...props} columns={columnsWithoutSort} />;
  }

  return (
    <EditableDataGrid {...props} onGridSort={handleGridSort} onRowSort={onRowSort} columns={columnsWithSortCell} />
  );
};
AbstractLineGrid.displayName = 'EditableDataGrid';
